import { Button, Divider, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import Sticky from 'react-sticky-el';
import BtnSave from './BtnSave';
import Editor from '../../../../components/Shared/forms/EditorPrivacy';
import TextBox from '../../Shared/TextBox';
import TableCookie from './TableCookie';
import { EXAMLE, INIT } from './init';
import { useEffect } from 'react';
import { checkValidForm } from './Step1';
import { POLICY_NAME } from '../../POLICY';
import EmptyCustom from '../../../../assets/Images/icons/emptyCustom';

const Step3 = (props) => {
    const { previewData, setStep3 } = props;
    const { t, i18n } = useTranslation(['translation', 'cookies-policy']);

    useEffect(() => {
        if (previewData) {
            let check = true;
            const allowKey = [
                'cookieHowDoTitleTh',
                'cookieHowDoTitleEn',
                'cookieHowDoContentTh',
                'cookieWhatTypeTitleTh',
                'cookieWhatTypeTitleEn',
            ];
            for (const [key, value] of Object.entries(
                previewData?.cookieAreUsed
            )) {
                if (allowKey.includes(key) && !value) {
                    check = false;
                }
            }
            setStep3(check);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewData]);

    const onNextStep = () => {
        props.nextStep();
        props?.form
            .validateFields()
            .then(() => {
                props.setStep3(true);
            })
            .catch(() => props.setStep3(false));
    };

    const onPreviousStep = () => {
        props.previousStep();
    };

    useEffect(() => {
        if (props?.previewData?.cookieAreUsed) {
            onValuesChange(null, props.previewData?.cookieAreUsed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.previewData]);

    const onValuesChange = (change, allValues) => {
        const fields = [
            'cookieHowDoTitleTh',
            'cookieHowDoTitleEn',
            'cookieHowDoContentTh',
            'cookieWhatTypeTitleTh',
            'cookieWhatTypeTitleEn',
        ];
        const check = checkValidForm(fields, allValues);
        props.setStep3(check);
    };

    const allCheck = (category) => {
        let check = category?.filter((res) => res?.isEnable);
        return check?.length;
    };

    const cookieCompanyNameTh =
        props.cookiePolicy?.cookieCompanyNameTh || '[company_name]';
    const cookieCompanyNameEn =
        props.cookiePolicy?.cookieCompanyNameEn || '[company_name]';

    return (
        <div className="px-4 block_">
            <div className="text-purple-PHX font-semibold text-2xl">
                {t(
                    'cookies-policy.How do we use cookies and what cookies are used',
                    { ns: POLICY_NAME.COOKIES_POLICY }
                )}
            </div>
            <div className="text-gray-500 text-tiny">{t('policy.desEtc')}</div>
            <Divider />
            <Form
                scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center',
                }}
                onValuesChange={onValuesChange}
                name="cookieAreUsed"
                layout="vertical"
                form={props?.form}
                initialValues={INIT.cookieAreUsed}
            >
                <div>
                    <div className="text-purple-PHX font-semibold text-xl">
                        {t('How do we use cookies', {
                            ns: POLICY_NAME.COOKIES_POLICY,
                        })}
                    </div>
                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'TH',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieHowDoTitleTh"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>
                    <div className="text-sm font-semibold">
                        {t('Example Data')}
                    </div>
                    <TextBox
                        code={EXAMLE.cookieAreUsed.cookieHowDoContentTh.replace(
                            /\{\{c\}\}/g,
                            cookieCompanyNameTh
                        )}
                    />
                    <Form.Item
                        className="mt-4"
                        name="cookieHowDoContentTh"
                        label={
                            <span className="text-sm font-semibold">
                                {t('Content', { lang: 'TH' })}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                    >
                        <Editor
                            size="large"
                            rows={4}
                            placeholder={t('Content', { lang: 'TH' })}
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'EN',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieHowDoTitleEn"
                    >
                        <Input size="large" />
                    </Form.Item>
                    <div className="text-sm font-semibold">
                        {t('Example Data')}
                    </div>
                    <TextBox
                        code={EXAMLE.cookieAreUsed.cookieHowDoContentEn.replace(
                            /\{\{c\}\}/g,
                            cookieCompanyNameEn
                        )}
                    />
                    <Form.Item
                        className="mt-4"
                        name="cookieHowDoContentEn"
                        label={
                            <span className="text-sm font-semibold">
                                {t('Content', { lang: 'EN' })}
                            </span>
                        }
                    >
                        <Editor
                            size="large"
                            rows={4}
                            placeholder={t('Content', { lang: 'EN' })}
                        />
                    </Form.Item>
                </div>
                <Divider />
                <div>
                    <div className="text-purple-PHX font-semibold text-xl">
                        {t('What types of cookies do we use', {
                            ns: POLICY_NAME.COOKIES_POLICY,
                        })}
                    </div>
                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'TH',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieWhatTypeTitleTh"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>
                    {props?.domainId ? (
                        <>
                            {props?.cookieList &&
                            allCheck(props?.cookieList?.cookieCategoryList) ? (
                                <div>
                                    {props?.cookieList?.cookieCategoryList?.map(
                                        (val) => (
                                            <div
                                                hidden={!val?.isEnable}
                                                className="mb-3"
                                            >
                                                <p className="text-[#65707A] font-semibold">
                                                    •{' '}
                                                    {val?.cookieCategoryNameTh}
                                                </p>
                                                <p className="text-[#65707A]">
                                                    {val?.descriptionTh}
                                                </p>
                                                <TableCookie
                                                    lang={'TH'}
                                                    listCookie={
                                                        val?.cookieList || []
                                                    }
                                                />
                                            </div>
                                        )
                                    )}
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <EmptyCustom
                                        text={
                                            i18n.language === 'en'
                                                ? `ไม่สามารถแสดงข้อมูลได้ เนื่องจากโดเมนนี้ปิดการแสดงประเภทคุกกี้อยู่<br>คุณสามารถเปิดการแสดงประเภทคุกกี้ได้ที่เมนู Cookie Management > Cookie Category`
                                                : `ไม่สามารถแสดงข้อมูลได้ เนื่องจากโดเมนนี้ปิดการแสดงประเภทคุกกี้อยู่<br>คุณสามารถเปิดการแสดงประเภทคุกกี้ได้ที่เมนู Cookie Management > Cookie Category`
                                        }
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <EmptyCustom
                                text={
                                    i18n.language === 'en'
                                        ? `Please specify the domain at step 1 : Define the policy name and organization information.`
                                        : `กรุณาระบุโดเมนที่ขั้นตอนที่ 1 : กำหนดชื่อนโยบายและข้อมูลองค์กร`
                                }
                            />
                        </>
                    )}
                    <Form.Item
                        label={
                            <span className="text-sm font-semibold">
                                {t(
                                    'Title of the topic displayed in the document',
                                    {
                                        ns: POLICY_NAME.COOKIES_POLICY,
                                        lang: 'EN',
                                    }
                                )}
                            </span>
                        }
                        rules={[
                            {
                                required: true,
                                message: t('errorText.requiredField'),
                            },
                        ]}
                        name="cookieWhatTypeTitleEn"
                    >
                        <Input size="large" maxLength={200} showCount />
                    </Form.Item>
                    <Form.Item hidden name="cookieHowDoIsDisplay"></Form.Item>
                    {props?.domainId ? (
                        <>
                            {props?.cookieList &&
                            allCheck(props?.cookieList?.cookieCategoryList) ? (
                                <>
                                    {props?.cookieList?.cookieCategoryList?.map(
                                        (val) => (
                                            <div
                                                hidden={!val?.isEnable}
                                                className="mb-3"
                                            >
                                                <p className="text-[#65707A] font-semibold">
                                                    •{' '}
                                                    {val?.cookieCategoryNameEn}
                                                </p>
                                                <p className="text-[#65707A]">
                                                    {val?.descriptionEn}
                                                </p>
                                                <TableCookie
                                                    lang={'EN'}
                                                    listCookie={
                                                        val?.cookieList || []
                                                    }
                                                />
                                            </div>
                                        )
                                    )}
                                </>
                            ) : (
                                <div className="mb-3">
                                    <EmptyCustom
                                        text={
                                            i18n.language === 'en'
                                                ? `ไม่สามารถแสดงข้อมูลได้ เนื่องจากโดเมนนี้ปิดการแสดงประเภทคุกกี้อยู่<br>คุณสามารถเปิดการแสดงประเภทคุกกี้ได้ที่เมนู Cookie Management > Cookie Category`
                                                : `ไม่สามารถแสดงข้อมูลได้ เนื่องจากโดเมนนี้ปิดการแสดงประเภทคุกกี้อยู่<br>คุณสามารถเปิดการแสดงประเภทคุกกี้ได้ที่เมนู Cookie Management > Cookie Category`
                                        }
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <EmptyCustom
                                text={
                                    i18n.language === 'en'
                                        ? `Please specify the domain at step 1 : Define the policy name and organization information.`
                                        : `กรุณาระบุโดเมนที่ขั้นตอนที่ 1 : กำหนดชื่อนโยบายและข้อมูลองค์กร`
                                }
                            />
                        </>
                    )}
                    <Form.Item
                        hidden
                        name="cookieWhatTypeIsDisplay"
                    ></Form.Item>
                </div>
                <Sticky
                    mode="bottom"
                    boundaryElement=".block_"
                    stickyClassName="bg-white p-4 shadow h-16 rounded-lg z-50"
                >
                    <div className="flex items-center">
                        <Button
                            type="default"
                            size="large"
                            onClick={onPreviousStep}
                        >
                            <ArrowLeftOutlined /> {t('policy.PreviousStep')}
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            className="ml-4"
                            onClick={onNextStep}
                        >
                            {t('policy.NextStep')} <ArrowRightOutlined />
                        </Button>
                        <BtnSave saveDraft={props.saveDraft} />
                    </div>
                </Sticky>
            </Form>
        </div>
    );
};

export default Step3;
